import { getApiLinkInfo } from "@lib/apiUtils";
import { comiciPlusClient } from "apiClient/ComiciPlusClient";
import { GLOBALS } from "config";
import { notFound, redirect } from "next/navigation";
import { SeriesBox } from "app/components/SeriesBox";
import { Pagination } from "app/components/Pagination";
import "styles/series.css";
import Link from "next/link";
import { getDayOfToday } from "@lib/utils";
import { CategoryPageTabs } from "../../components/CategoryPageTabs";
import { GetSeriesDaysOfWeekEnum } from "@api/apis";
import { Metadata } from "next";
import { SeriesSummaryWithCampaignInfo } from "@api/models";
import { logError } from "@lib/logger";
import { ErrorDisplay } from "app/components/ErrorDisplay";

export type CategoryName = "manga" | "gravure" | "column";
type CategoryPageParams = {
  categoryName: CategoryName;
  page?: string;
  dayId?: string;
};

export type PageStatus = "continue" | "complete" | "oneShot";

export const CAT_NAME = {
  manga: "マンガ",
  gravure: "グラビア",
  column: "記事",
};
const STAT_NAME = {
  continue: "連載",
  oneShot: "読み切り",
  complete: "完結",
};
export const DAYS = ["月", "火", "水", "木", "金", "土", "日", "その他"];
const DAYS_API = [
  GetSeriesDaysOfWeekEnum.Mon,
  GetSeriesDaysOfWeekEnum.Tue,
  GetSeriesDaysOfWeekEnum.Wed,
  GetSeriesDaysOfWeekEnum.Thu,
  GetSeriesDaysOfWeekEnum.Fri,
  GetSeriesDaysOfWeekEnum.Sat,
  GetSeriesDaysOfWeekEnum.Sun,
  GetSeriesDaysOfWeekEnum.Other,
];

export function getHasWeekNav(categoryName: CategoryName, status: PageStatus): boolean {
  return !!(
    status === "continue" &&
    ((categoryName === "manga" && GLOBALS.HAS_MANGA_WEEK_NAV) ||
      (categoryName === "gravure" && GLOBALS.HAS_GRAVURE_WEEK_NAV) ||
      (categoryName === "column" && GLOBALS.HAS_COLUMN_WEEK_NAV))
  );
}
export function getCategoryMetaFunction(suffix: string = "") {
  return async (props: { params: Promise<{ categoryName: string }> }): Promise<Metadata> => {
    const params = await props.params;
    let title = "";
    if (params.categoryName === "manga") {
      title = CAT_NAME.manga;
    } else if (params.categoryName === "gravure") {
      title = CAT_NAME.gravure;
    } else if (params.categoryName === "column") {
      title = CAT_NAME.column;
    }
    return { title: title + suffix };
  };
}

export function getCategoryPage(status: PageStatus = "continue") {
  return async function CategoryPage(props: { params: Promise<CategoryPageParams> }) {
    const params = await props.params;
    if (!GLOBALS.HAS_GRAVURE && params.categoryName === "gravure") {
      notFound();
    }

    const title = CAT_NAME[params.categoryName];

    //カテゴリ指定不正の場合は404
    if (!title) {
      notFound();
    }

    //pageに「complete」「oneShot」が入ってる＝/category/manga/onShot等ページ指定漏れなのでリダイレクト
    if (
      status === "continue" &&
      params.page &&
      (params.page === "complete" || params.page === "oneShot")
    ) {
      redirect(`/category/${params.categoryName}/${params.page}/1`);
    }

    const dayId = params.dayId ? parseInt(params.dayId, 10) : undefined;
    const hasWeekNav = getHasWeekNav(params.categoryName, status);
    const day = getDayOfToday();

    //dayIdの指定がない時は自動で今日の曜日にリダイレクトさせる
    if (hasWeekNav && dayId === undefined) {
      redirect(`/category/${params.categoryName}/day/${day}/1`);
    }
    //dayIdが指定されててpageがないときは1ページ目にリダイレクト
    if (hasWeekNav && dayId && !params.page) {
      redirect(`/category/${params.categoryName}/day/${dayId}/1`);
    }

    const page = params.page ? parseInt(params.page, 10) : 1;

    //作品取得
    let seriesList: SeriesSummaryWithCampaignInfo[] = [];
    let lastPage: number = 0;
    try {
      const response = await comiciPlusClient.contents.getSeriesRaw(
        {
          page: page,
          size: 24,
          status: status,
          sort: "updatedOn",
          category: params.categoryName,
          daysOfWeek: dayId !== undefined ? DAYS_API[dayId - 1] : undefined,
        },
        { next: { revalidate: GLOBALS.DEFAULT_CACHE_TIME } },
      );
      seriesList = await response.value();
      lastPage = getApiLinkInfo(response).lastPage;
    } catch (error: unknown) {
      if (error instanceof Error) {
        logError("Failed to fetch series: " + error.message);
      } else {
        logError("Failed to fetch series: unknown error");
      }
      return <ErrorDisplay message="作品の取得に失敗しました" />;
    }

    let pages: { url: string; pageId: number }[] = [];
    let baseUrl = `/category/${params.categoryName}/`;
    if (status === "complete" || status === "oneShot") {
      baseUrl += status;
    } else if (dayId) {
      baseUrl += `day/${dayId}`;
    }
    pages = new Array(lastPage).fill(0).map((_, i) => ({
      url: `${baseUrl}/${i + 1}`,
      pageId: i + 1,
    }));

    return (
      <div className="genre">
        <CategoryPageTabs status={status} categoryName={params.categoryName} day={day} />
        {hasWeekNav && (
          <div className="g-tab-wrap">
            <nav className="g-tab mode-week">
              {DAYS.map((item, idx) => {
                return (
                  <Link
                    className={"g-tab-item" + (dayId === idx + 1 ? " mode-current" : "")}
                    href={`/category/${params.categoryName}/day/${idx + 1}/1`}
                    key={idx}
                  >
                    {item}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
        <h1 className="g-h">
          {dayId !== undefined && <>{DAYS[dayId - 1]}曜日の</>}
          {STAT_NAME[status]}
          {title}（更新順）
        </h1>
        {!seriesList.length && (
          <div className="series-list mode-empty">該当する作品がありません</div>
        )}

        {seriesList.length > 0 && (
          <div className="series-list">
            {seriesList.map((series, idx) => (
              <SeriesBox
                series={series}
                key={series.id}
                displayIndex={idx}
                displayedOn="category"
              />
            ))}
          </div>
        )}

        <Pagination pages={pages} activePage={page} />
      </div>
    );
  };
}
